import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack,
  ArrowOutward,
  AutoStories,
  Brightness4,
  Brightness7,
  CheckCircleOutline,
  Home,
  Insights,
  MenuBook,
  ReportProblemOutlined,
  Search,
  ShieldOutlined,
} from '@mui/icons-material';

const SITE_NAME = 'CarrierCheck';
const AUTHOR_NAME = 'King Dispatch LLC';
const SITE_URL = 'https://carrierchk.com';

const categoryStyles = {
  'Carrier Vetting': {
    code: 'CV',
    accent: '#0f766e',
    gradient: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
    tint: 'rgba(20, 184, 166, 0.12)',
    Icon: ShieldOutlined,
  },
  'FMCSA Basics': {
    code: 'FB',
    accent: '#2563eb',
    gradient: 'linear-gradient(135deg, #1d4ed8 0%, #60a5fa 100%)',
    tint: 'rgba(59, 130, 246, 0.12)',
    Icon: AutoStories,
  },
  'Authority & Insurance': {
    code: 'AI',
    accent: '#7c3aed',
    gradient: 'linear-gradient(135deg, #6d28d9 0%, #a78bfa 100%)',
    tint: 'rgba(124, 58, 237, 0.12)',
    Icon: CheckCircleOutline,
  },
  'FMCSA Guides': {
    code: 'FG',
    accent: '#ea580c',
    gradient: 'linear-gradient(135deg, #c2410c 0%, #fb923c 100%)',
    tint: 'rgba(251, 146, 60, 0.14)',
    Icon: Insights,
  },
  'Freight Safety': {
    code: 'FS',
    accent: '#b91c1c',
    gradient: 'linear-gradient(135deg, #991b1b 0%, #f87171 100%)',
    tint: 'rgba(248, 113, 113, 0.14)',
    Icon: ReportProblemOutlined,
  },
};

const posts = [
  {
    id: 'verify-carrier-before-booking',
    title: 'How to Verify a Carrier Before Booking a Load',
    category: 'Carrier Vetting',
    readTime: '7 min read',
    published: 'March 2026',
    summary: 'A repeatable broker workflow for checking authority, insurance, identity, and operational fit before dispatch.',
    excerpt: 'Good broker operations do not approve carriers on confidence or pressure. They approve them on a sequence that can be explained and repeated.',
    heroLabel: 'Carrier Verification System',
    learn: [
      'How to run a clean pre-booking approval sequence',
      'Which checks should happen before rate discussion',
      'What patterns justify escalation or rejection',
    ],
    sections: [
      {
        heading: 'Start with legal authority',
        body: [
          'Open FMCSA Licensing and Insurance first. Confirm that the carrier has active operating authority for the exact type of move you need.',
          'A visible carrier record is not enough. If authority is inactive, pending, or not authorized, the file is already disqualified.',
        ],
      },
      {
        heading: 'Verify insurance against the load',
        body: [
          'Check cargo and liability coverage, then compare those limits to shipment value and theft exposure. Standard minimums do not make every load safe.',
          'For higher-risk freight, verify coverage directly with the listed insurance contact instead of relying only on a forwarded certificate.',
        ],
        checklist: [
          'OK: Confirm the policy is active',
          'OK: Match insured name to the legal carrier entity',
          'OK: Escalate when freight value exceeds coverage',
          'Avoid: Treating a PDF alone as final proof',
        ],
      },
      {
        heading: 'Control the identity channel',
        body: [
          'Use SAFER and FMCSA-listed phone numbers to verify company details, dispatcher identity, and contact consistency.',
          'Fraud becomes easier when the other side controls the communication path. A clean callback process removes that advantage.',
        ],
      },
    ],
    steps: [
      'Confirm USDOT and MC authority status.',
      'Review cargo and liability insurance.',
      'Compare fleet footprint with the promised capacity.',
      'Call the FMCSA-listed phone number to verify identity.',
      'Review safety and fraud signals before tendering the load.',
    ],
    takeaway: [
      'Authority is the first gate, not the last one.',
      'Insurance and identity must be verified together.',
      'Multiple small inconsistencies usually point to a larger operational risk.',
    ],
    warningTitle: 'Pro Tip',
    warningText: 'If your team cannot explain why a carrier was approved in one sentence, the process is still too loose.',
  },
  {
    id: 'what-is-usdot-number',
    title: 'What Is a USDOT Number and Why Does It Matter?',
    category: 'FMCSA Basics',
    readTime: '5 min read',
    published: 'March 2026',
    summary: 'A practical explanation of what the USDOT number tells a broker during first-pass verification.',
    excerpt: 'The USDOT number is not just an identifier. It is the front door into a carrier’s operating and safety record.',
    heroLabel: 'USDOT Fundamentals',
    learn: [
      'What the USDOT number is used for',
      'Why missing or mismatched DOT data is a serious problem',
      'How brokers use it during initial screening',
    ],
    sections: [
      {
        heading: 'What the number represents',
        body: [
          'A USDOT number is the federal identifier that links a carrier to inspections, crashes, audits, and operating details.',
          'For a broker, it is the fastest way to connect a claimed company identity with federal safety context.',
        ],
      },
      {
        heading: 'Why it matters in practice',
        body: [
          'The DOT number helps you confirm company name, phone, address, fleet size, and inspection activity against what the carrier claims.',
          'If the DOT record does not match the file in front of you, do not treat that as a paperwork issue. Treat it as a risk signal.',
        ],
        checklist: [
          'OK: Search SAFER by DOT number',
          'OK: Match legal name, address, and phone',
          'OK: Review inspections and operating profile',
          'Avoid: Proceeding on verbal explanations alone',
        ],
      },
    ],
    takeaway: [
      'The DOT number is your first identity checkpoint.',
      'A valid DOT record adds safety context to the file.',
      'If the data does not match, pause and verify again.',
    ],
    warningTitle: 'Warning',
    warningText: 'A missing DOT number is not a harmless omission. It is a reason to stop the file until verified.',
  },
  {
    id: 'check-active-authority',
    title: 'How to Check If a Carrier Has Active Authority',
    category: 'Authority & Insurance',
    readTime: '5 min read',
    published: 'March 2026',
    summary: 'Where to check authority, what each status means, and what brokers often misunderstand.',
    excerpt: 'Many teams confuse a visible carrier record with legally usable authority. That mistake is expensive.',
    heroLabel: 'Authority Validation',
    learn: [
      'Which FMCSA database to use',
      'How to interpret active, inactive, and not-authorized results',
      'Why active authority is necessary but still not enough',
    ],
    sections: [
      {
        heading: 'Use the correct source',
        body: [
          'Authority status should be checked in FMCSA Licensing and Insurance. SAFER is useful, but it is not the final legal authority screen.',
          'Look for active authority supported by the required filings. If the status is inactive or not authorized, do not book the load.',
        ],
      },
      {
        heading: 'What active really tells you',
        body: [
          'Active authority means the record is currently usable for the relevant operation. It does not mean the carrier is safe, low-risk, or operationally strong.',
          'Think of it as the legal entrance requirement. Full approval still requires insurance, safety, identity, and fit review.',
        ],
      },
    ],
    table: {
      headers: ['Status', 'Meaning', 'Broker Action'],
      rows: [
        ['Active', 'Legally usable authority with required support filings', 'Continue full vetting'],
        ['Inactive', 'Authority is not currently usable', 'Reject until resolved'],
        ['Not Authorized', 'Carrier cannot legally operate under that authority', 'Do not book'],
      ],
    },
    takeaway: [
      'Check authority in Licensing and Insurance.',
      'Active is required, but not enough by itself.',
      'A visible record is not the same as a usable authority.',
    ],
    warningTitle: 'Warning',
    warningText: 'Do not let “we are fixing the filing” override what the live FMCSA record currently shows.',
  },
  {
    id: 'seven-red-flags-new-carrier',
    title: '7 Red Flags When Vetting a New Carrier',
    category: 'Carrier Vetting',
    readTime: '6 min read',
    published: 'February 2026',
    summary: 'The signals that should slow a file down, trigger escalation, or justify a hard stop.',
    excerpt: 'One issue may be explainable. Several at once usually mean the file is telling the truth before the dispatcher does.',
    heroLabel: 'Red Flag Detection',
    learn: [
      'Which signals matter most',
      'How to separate startup risk from fraud risk',
      'When to escalate instead of auto-rejecting',
    ],
    sections: [
      {
        heading: 'Read the pattern, not one isolated fact',
        body: [
          'New authority, weak insurance clarity, fleet mismatch, poor safety patterns, recent identity changes, and pressure tactics should all slow the file down.',
          'These signals do not carry equal weight, but they become more dangerous when several of them appear together.',
        ],
      },
    ],
    steps: [
      'New authority with little operating history',
      'No clear or active insurance support',
      'Poor safety or inspection patterns',
      'High out-of-service percentages',
      'Recent changes to contact or address details',
      'Fleet mismatch or weak equipment fit',
      'Pressure to book before verification is complete',
    ],
    takeaway: [
      'Pattern recognition matters more than one isolated issue.',
      'Pressure is itself a useful risk signal.',
      'When multiple red flags align, slow down or reject.',
    ],
    warningTitle: 'Warning',
    warningText: 'Speed from the carrier should never force speed from your vetting team.',
  },
  {
    id: 'read-fmcsa-safety-rating',
    title: 'How to Read an FMCSA Safety Rating',
    category: 'FMCSA Guides',
    readTime: '6 min read',
    published: 'February 2026',
    summary: 'A broker-friendly guide to satisfactory, conditional, and unsatisfactory ratings.',
    excerpt: 'The FMCSA safety rating is useful, but it is not current enough to replace trend review and operational judgment.',
    heroLabel: 'Safety Rating Guide',
    learn: [
      'What each rating means',
      'How to react to each status',
      'Why ratings should be paired with current inspection data',
    ],
    sections: [
      {
        heading: 'Use the rating as one signal',
        body: [
          'Treat the safety rating as one input, not the whole decision. It reflects a prior compliance review, not necessarily current operating behavior.',
          'A satisfactory rating does not make the carrier perfect. A conditional rating means the file deserves deeper attention.',
        ],
      },
    ],
    table: {
      headers: ['Rating', 'Meaning', 'Recommended Action'],
      rows: [
        ['Satisfactory', 'Basic safety controls were found acceptable', 'Continue normal vetting'],
        ['Conditional', 'Safety deficiencies were identified', 'Escalate and review more deeply'],
        ['Unsatisfactory', 'Serious safety management failure', 'Do not book'],
      ],
    },
    takeaway: [
      'Satisfactory is acceptable, not flawless.',
      'Conditional should trigger escalation.',
      'Unsatisfactory should be treated as a hard stop.',
    ],
    warningTitle: 'Pro Tip',
    warningText: 'Use rating plus inspection pattern plus out-of-service trend. One data point is never enough.',
  },
  {
    id: 'cargo-insurance-explained',
    title: 'Cargo Insurance Explained: How Much Is Enough?',
    category: 'Authority & Insurance',
    readTime: '6 min read',
    published: 'February 2026',
    summary: 'How brokers should think about cargo limits, commodity value, and fake insurance certificates.',
    excerpt: 'The real question is not whether the carrier has cargo insurance. The question is whether the coverage fits the load.',
    heroLabel: 'Cargo Coverage Review',
    learn: [
      'Why minimum coverage is often not enough',
      'How to match policy value to freight value',
      'How fake certificates usually show up',
    ],
    sections: [
      {
        heading: 'Minimum does not mean sufficient',
        body: [
          'Many teams use $75,000 as a default cargo benchmark, but higher-value shipments can exceed that immediately.',
          'Your review should consider commodity value, theft exposure, customer requirements, and total shipment exposure, not just a standard default number.',
        ],
      },
      {
        heading: 'Verify cleanly',
        body: [
          'Match the insured name to the exact legal carrier entity and confirm policy status. For sensitive freight, call the listed insurer or agent directly.',
          'Fake certificates are still common. Cropped PDFs, strange formatting, and mismatched insured names are all reasons to verify further.',
        ],
        checklist: [
          'OK: Match coverage to shipment value',
          'OK: Confirm the insured legal name',
          'OK: Call the listed insurance contact when needed',
          'Avoid: Relying on screenshots or altered PDFs',
        ],
      },
    ],
    takeaway: [
      'Coverage should match cargo exposure.',
      'A certificate is not the same as confirmed coverage.',
      'High-value freight deserves direct insurance verification.',
    ],
    warningTitle: 'Warning',
    warningText: 'If the freight is worth more than the policy limit, you are already carrying unpriced risk.',
  },
  {
    id: 'spot-freight-broker-scam',
    title: 'How to Spot a Freight Broker Scam',
    category: 'Freight Safety',
    readTime: '7 min read',
    published: 'January 2026',
    summary: 'A practical guide to identifying identity theft, spoofed domains, double brokering, and fake paperwork.',
    excerpt: 'Freight scams usually succeed because the scammer controls urgency, confusion, or the communication channel.',
    heroLabel: 'Fraud Detection',
    learn: [
      'Which scam patterns appear most often',
      'How to verify the contact behind the paperwork',
      'What to check before releasing full load details',
    ],
    sections: [
      {
        heading: 'The common scam patterns',
        body: [
          'Double brokering, carrier identity theft, spoofed email domains, fake insurance documents, and false pickup instructions remain high-frequency risks.',
          'The fastest way to reduce scam exposure is to verify identity through an independent source instead of the contact information the scammer provided.',
        ],
        checklist: [
          'OK: Use FMCSA-listed phone numbers',
          'OK: Match domain, name, and legal entity',
          'OK: Confirm who will physically pick up the freight',
          'Avoid: Sending full load details before verification',
        ],
      },
    ],
    takeaway: [
      'Fraud usually attacks weak process, not smart people.',
      'Identity verification matters as much as authority verification.',
      'If urgency rises while clarity falls, risk is increasing.',
    ],
    warningTitle: 'Warning',
    warningText: 'A polished email signature is not evidence. Federal records and controlled callbacks are.',
  },
  {
    id: 'complete-carrier-vetting-guide-2026',
    title: 'Freight Broker Complete Carrier Vetting Guide 2026',
    category: 'Carrier Vetting',
    readTime: '10 min read',
    published: 'January 2026',
    summary: 'An end-to-end framework for brokers who want a repeatable and audit-friendly vetting process.',
    excerpt: 'The best vetting systems do not rely on instinct. They rely on sequence, thresholds, and documentation.',
    heroLabel: 'Complete Broker Workflow',
    learn: [
      'How to build a full carrier vetting system',
      'Which free tools support the process',
      'How to score carriers consistently across a team',
    ],
    sections: [
      {
        heading: 'Build the process in sequence',
        body: [
          'Start with identity and authority. Move into insurance, then safety, then contact verification, then equipment and lane fit.',
          'That order gives your team a clean operating rhythm and produces better files for audits, claims, and internal review.',
        ],
      },
      {
        heading: 'Make the system team-proof',
        body: [
          'Use a standard checklist, define risk tiers, and record exception reasons. A reliable process should still work when your busiest dispatcher is unavailable.',
        ],
      },
    ],
    steps: [
      'Confirm USDOT and legal identity.',
      'Confirm active operating authority.',
      'Verify cargo and liability coverage.',
      'Review safety, inspections, and out-of-service trends.',
      'Verify contact identity and pickup chain.',
      'Confirm fleet and equipment fit.',
      'Assign a risk level and document the decision.',
    ],
    table: {
      headers: ['Risk Level', 'Typical Profile', 'Action'],
      rows: [
        ['Low', 'Stable authority, clean identity match, acceptable safety, adequate insurance', 'Book with standard controls'],
        ['Medium', 'Thin history or limited issues requiring review', 'Escalate and tighten controls'],
        ['High', 'Inactive status, identity mismatch, missing filings, strong fraud or safety concerns', 'Reject'],
      ],
    },
    takeaway: [
      'A process beats instinct.',
      'Every approval should have a documented reason.',
      'Consistency is what makes the system scalable.',
    ],
    warningTitle: 'Pro Tip',
    warningText: 'If your team approves carriers differently on different shifts, you do not have a system yet.',
  },
];

function ensureMeta(selector, attributes) {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement('meta');
    document.head.appendChild(node);
  }
  Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
}

function ensureLink(selector, attributes) {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement('link');
    document.head.appendChild(node);
  }
  Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
}

function ensureJsonLd(data) {
  let node = document.head.querySelector('script[data-blog-jsonld="true"]');
  if (!node) {
    node = document.createElement('script');
    node.type = 'application/ld+json';
    node.setAttribute('data-blog-jsonld', 'true');
    document.head.appendChild(node);
  }
  node.textContent = JSON.stringify(data);
}

function makeArtDataUri(post) {
  const style = categoryStyles[post.category];
  const title = encodeURIComponent(post.title);
  const category = encodeURIComponent(post.category);
  const label = encodeURIComponent(post.heroLabel);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="1200" height="720" viewBox="0 0 1200 720">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#07111f"/>
          <stop offset="55%" stop-color="${style.accent}"/>
          <stop offset="100%" stop-color="#020617"/>
        </linearGradient>
      </defs>
      <rect width="1200" height="720" fill="url(#g)"/>
      <circle cx="170" cy="120" r="190" fill="rgba(255,255,255,0.08)"/>
      <circle cx="1030" cy="600" r="240" fill="rgba(255,255,255,0.07)"/>
      <path d="M770 90h260c44 0 80 36 80 80v180c0 44-36 80-80 80H770c-44 0-80-36-80-80V170c0-44 36-80 80-80Z" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.16)"/>
      <text x="820" y="285" fill="white" font-size="112" font-family="Arial, sans-serif" font-weight="800">${style.code}</text>
      <rect x="88" y="88" width="270" height="54" rx="27" fill="rgba(255,255,255,0.14)"/>
      <text x="116" y="124" fill="white" font-size="24" font-family="Arial, sans-serif" font-weight="700">${category}</text>
      <text x="88" y="258" fill="white" font-size="68" font-family="Arial, sans-serif" font-weight="800">${title}</text>
      <text x="88" y="560" fill="rgba(255,255,255,0.82)" font-size="31" font-family="Arial, sans-serif">${label}</text>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function slugify(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function normalizeCopy(value) {
  if (Array.isArray(value)) return value.map(normalizeCopy);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, normalizeCopy(entry)]));
  }
  if (typeof value !== 'string') return value;

  return value
    .replace(/â€™/g, "'")
    .replace(/â€œ/g, '"')
    .replace(/â€/g, '"')
    .replace(/â€“/g, '-')
    .replace(/â€”/g, '-')
    .replace(/â€¦/g, '...')
    .replace(/â€\u00a2/g, '•');
}

function useSelectedPost(postsList) {
  const [selectedId, setSelectedId] = useState(() => {
    if (typeof window === 'undefined') return postsList[0]?.id || '';
    const hash = window.location.hash.replace('#', '');
    return postsList.some((post) => post.id === hash) ? hash : postsList[0]?.id || '';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (postsList.some((post) => post.id === hash)) setSelectedId(hash);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [postsList]);

  const selectedPost = postsList.find((post) => post.id === selectedId) || postsList[0];

  const openPost = (id) => {
    setSelectedId(id);
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', `#${id}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return { selectedPost, openPost };
}

function getChecklistTone(item) {
  if (item.startsWith('Avoid:')) return 'warning';
  return 'success';
}

const BlogPage = ({ toggleDarkMode }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));
  const [search, setSearch] = useState('');
  const normalizedPosts = useMemo(() => normalizeCopy(posts), []);

  const filteredPosts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return normalizedPosts;
    return normalizedPosts.filter((post) =>
      [
        post.title,
        post.category,
        post.summary,
        post.excerpt,
        post.published,
        ...(post.learn || []),
        ...(post.takeaway || []),
        ...(post.sections || []).flatMap((section) => [section.heading, ...(section.body || []), ...(section.checklist || [])]),
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [normalizedPosts, search]);

  const selectablePosts = filteredPosts.length > 0 ? filteredPosts : normalizedPosts;
  const { selectedPost, openPost } = useSelectedPost(selectablePosts);
  const activePost = selectedPost;

  useEffect(() => {
    if (!activePost || typeof window === 'undefined') return;
    const canonicalUrl = `${SITE_URL}/blog/${activePost.id}`;
    document.title = `${activePost.title} | ${SITE_NAME}`;
    ensureMeta('meta[name="description"]', { name: 'description', content: activePost.summary });
    ensureMeta('meta[property="og:title"]', { property: 'og:title', content: activePost.title });
    ensureMeta('meta[property="og:description"]', { property: 'og:description', content: activePost.summary });
    ensureMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    ensureMeta('meta[property="og:type"]', { property: 'og:type', content: 'article' });
    ensureMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    ensureMeta('meta[name="robots"]', { name: 'robots', content: 'index,follow,max-image-preview:large' });
    ensureLink('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl });
    ensureJsonLd({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: activePost.title,
      description: activePost.summary,
      author: { '@type': 'Organization', name: AUTHOR_NAME },
      publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    });
  }, [activePost]);

  const surface = useMemo(
    () => ({
      page: isDark
        ? 'radial-gradient(circle at top left, rgba(20,184,166,0.16) 0%, transparent 24%), radial-gradient(circle at top right, rgba(59,130,246,0.16) 0%, transparent 22%), linear-gradient(180deg, #07111f 0%, #0b1323 100%)'
        : 'radial-gradient(circle at top left, rgba(20,184,166,0.12) 0%, transparent 22%), radial-gradient(circle at top right, rgba(59,130,246,0.14) 0%, transparent 22%), linear-gradient(180deg, #f5f7fb 0%, #eef4f7 100%)',
      card: isDark ? alpha('#09111f', 0.82) : 'rgba(255,255,255,0.82)',
      panel: isDark ? alpha('#0f172a', 0.76) : 'rgba(255,255,255,0.9)',
      soft: isDark ? alpha('#ffffff', 0.04) : '#f7fafc',
      border: isDark ? alpha('#e2e8f0', 0.10) : 'rgba(15, 23, 42, 0.08)',
      title: isDark ? '#f8fafc' : '#0f172a',
      body: isDark ? 'rgba(226,232,240,0.8)' : '#475569',
      muted: isDark ? 'rgba(148,163,184,0.9)' : '#64748b',
      shadow: isDark ? '0 28px 80px rgba(2, 8, 23, 0.36)' : '0 30px 80px rgba(15, 23, 42, 0.10)',
    }),
    [isDark]
  );

  if (!activePost) {
    return null;
  }

  const activeStyle = categoryStyles[activePost.category];
  const featuredPosts = filteredPosts.slice(0, 3);
  const morePosts = normalizedPosts.filter((post) => post.id !== activePost.id).slice(0, 3);
  const relatedPosts = normalizedPosts.filter((post) => post.id !== activePost.id).slice(0, 3);
  const articleSections = activePost.sections.map((section) => ({
    ...section,
    slug: slugify(section.heading),
  }));

  return (
    <Box sx={{ minHeight: '100vh', background: surface.page }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 30,
          backdropFilter: 'blur(18px)',
          backgroundColor: isDark ? alpha('#07111f', 0.76) : alpha('#f5f7fb', 0.78),
          borderBottom: '1px solid',
          borderColor: surface.border,
        }}
      >
        <Container maxWidth="xl" sx={{ py: 1.4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 3,
                  display: 'grid',
                  placeItems: 'center',
                  color: '#fff',
                  fontWeight: 900,
                  letterSpacing: '-0.04em',
                  fontFamily: '"Sora", sans-serif',
                  background: 'linear-gradient(135deg, #0f172a 0%, #2563eb 45%, #14b8a6 100%)',
                  boxShadow: '0 14px 30px rgba(37, 99, 235, 0.24)',
                }}
              >
                CK
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 900, letterSpacing: '-0.04em', fontFamily: '"Sora", sans-serif', color: surface.title }}>
                  {SITE_NAME}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: surface.muted, textTransform: 'uppercase', letterSpacing: '0.16em' }}>
                  A {AUTHOR_NAME} service
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={{ xs: 0.5, md: 1 }} alignItems="center" flexWrap="wrap" useFlexGap>
              {[
                { label: 'Search', href: '/', icon: <Home fontSize="small" /> },
                { label: 'Blog', href: '/blog', icon: <MenuBook fontSize="small" /> },
                { label: 'Bulk Search', href: '/' },
                { label: 'About', href: '/' },
                { label: 'FAQ', href: '/' },
                { label: 'Contact', href: '/' },
              ].map((link) => (
                <Button
                  key={link.label}
                  href={link.href}
                  startIcon={link.icon}
                  variant={link.label === 'Blog' ? 'contained' : 'text'}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 999,
                    px: 1.7,
                    color: link.label === 'Blog' ? '#fff' : surface.body,
                    background: link.label === 'Blog' ? 'linear-gradient(135deg, #2563eb 0%, #0f766e 100%)' : 'transparent',
                  }}
                >
                  {link.label}
                </Button>
              ))}
              <IconButton onClick={toggleDarkMode} sx={{ border: '1px solid', borderColor: surface.border, backgroundColor: surface.panel }}>
                {isDark ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        <Card
          sx={{
            borderRadius: 7,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: surface.border,
            background: isDark
              ? 'linear-gradient(135deg, rgba(8,15,28,0.94) 0%, rgba(15,118,110,0.18) 52%, rgba(37,99,235,0.16) 100%)'
              : 'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(219,234,254,0.92) 50%, rgba(204,251,241,0.92) 100%)',
            boxShadow: surface.shadow,
            mb: 3,
          }}
        >
          <Box sx={{ p: { xs: 3, md: 5 } }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.35fr) 360px' },
                gap: 3,
                alignItems: 'start',
              }}
            >
              <Box>
                <Chip
                  label="Updated editorial layout"
                  sx={{
                    mb: 2,
                    borderRadius: 999,
                    fontWeight: 800,
                    color: isDark ? '#bfdbfe' : '#1d4ed8',
                    backgroundColor: isDark ? 'rgba(37,99,235,0.16)' : 'rgba(37,99,235,0.08)',
                  }}
                />
                <Typography
                  component="h1"
                  sx={{
                    maxWidth: 760,
                    fontSize: { xs: '2.5rem', md: '4.8rem' },
                    lineHeight: 0.95,
                    letterSpacing: '-0.07em',
                    fontWeight: 900,
                    fontFamily: '"Sora", sans-serif',
                    color: surface.title,
                    mb: 1.6,
                  }}
                >
                  A cleaner, more readable
                  <br />
                  freight operations blog
                </Typography>
                <Typography sx={{ maxWidth: 760, color: surface.body, lineHeight: 1.9, fontSize: { xs: '1rem', md: '1.05rem' }, mb: 2.6 }}>
                  The blog now behaves like an editorial product instead of a raw article dump: stronger hierarchy, clearer discovery, calmer spacing, and a reading column that is easier to scan on both desktop and mobile.
                </Typography>
                <TextField
                  fullWidth
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search vetting, authority, insurance, scams, FMCSA..."
                  sx={{
                    maxWidth: 560,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 999,
                      backgroundColor: isDark ? alpha('#ffffff', 0.04) : '#ffffff',
                    },
                  }}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: surface.muted }} />,
                  }}
                />
              </Box>
              <Card
                sx={{
                  borderRadius: 5,
                  border: '1px solid',
                  borderColor: surface.border,
                  backgroundColor: surface.panel,
                  backdropFilter: 'blur(12px)',
                }}
              >
                <CardContent sx={{ p: 2.4 }}>
                  <Typography sx={{ mb: 2, fontWeight: 900, color: surface.title, fontFamily: '"Sora", sans-serif' }}>
                    Why this layout reads better
                  </Typography>
                  <Stack spacing={1.4}>
                    {[
                      'Stronger title and excerpt hierarchy',
                      'Focused reading column with cleaner line length',
                      'Category-led discovery instead of visual clutter',
                      'Professional cards and article navigation',
                    ].map((item) => (
                      <Box key={item} sx={{ display: 'flex', gap: 1.2 }}>
                        <CheckCircleOutline sx={{ mt: '2px', color: '#14b8a6', fontSize: 20 }} />
                        <Typography sx={{ color: surface.body, lineHeight: 1.75 }}>{item}</Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Divider sx={{ my: 2, borderColor: surface.border }} />
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {Object.entries(categoryStyles).map(([name, style]) => {
                      const Icon = style.Icon;
                      return (
                        <Chip
                          key={name}
                          icon={<Icon sx={{ color: `${style.accent} !important` }} />}
                          label={name}
                          sx={{
                            borderRadius: 999,
                            backgroundColor: style.tint,
                            color: surface.title,
                            border: '1px solid',
                            borderColor: surface.border,
                          }}
                        />
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Card>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '320px minmax(0, 1fr)' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          <Stack spacing={2.2} sx={{ position: isCompact ? 'static' : 'sticky', top: 96 }}>
            <Card sx={{ borderRadius: 5, border: '1px solid', borderColor: surface.border, backgroundColor: surface.panel }}>
              <CardContent sx={{ p: 2.3 }}>
                <Typography sx={{ mb: 1.5, fontWeight: 900, color: surface.title, fontFamily: '"Sora", sans-serif' }}>
                  Browse Articles
                </Typography>
                <Stack spacing={1}>
                  {filteredPosts.map((post) => {
                    const style = categoryStyles[post.category];
                    const Icon = style.Icon;
                    const active = post.id === activePost.id;
                    return (
                      <Button
                        key={post.id}
                        onClick={() => openPost(post.id)}
                        sx={{
                          justifyContent: 'flex-start',
                          alignItems: 'flex-start',
                          textAlign: 'left',
                          gap: 1.2,
                          px: 1.3,
                          py: 1.25,
                          borderRadius: 3.5,
                          textTransform: 'none',
                          color: active ? '#fff' : surface.title,
                          background: active ? style.gradient : surface.soft,
                          border: '1px solid',
                          borderColor: active ? 'transparent' : surface.border,
                        }}
                      >
                        <Icon sx={{ mt: '3px', fontSize: 19, color: active ? '#fff' : style.accent }} />
                        <Box>
                          <Typography sx={{ fontSize: '0.74rem', fontWeight: 900, opacity: active ? 0.88 : 0.72, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            {post.category}
                          </Typography>
                          <Typography sx={{ mt: 0.3, fontWeight: 800, lineHeight: 1.35 }}>
                            {post.title}
                          </Typography>
                        </Box>
                      </Button>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 5, border: '1px solid', borderColor: surface.border, backgroundColor: surface.panel }}>
              <CardContent sx={{ p: 2.3 }}>
                <Typography sx={{ mb: 1.4, fontWeight: 900, color: surface.title }}>Reading Focus</Typography>
                <Stack spacing={1}>
                  {[
                    'Verification workflows',
                    'Authority and insurance reviews',
                    'Safety interpretation',
                    'Fraud and double brokering defense',
                  ].map((item) => (
                    <Typography key={item} sx={{ color: surface.body, lineHeight: 1.7 }}>
                      {item}
                    </Typography>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Stack>

          <Stack spacing={3}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2.2 }}>
              {featuredPosts.map((post) => {
                const style = categoryStyles[post.category];
                const Icon = style.Icon;
                return (
                  <Card
                    key={post.id}
                    onClick={() => openPost(post.id)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 5,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: surface.border,
                      backgroundColor: surface.panel,
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: surface.shadow,
                      },
                    }}
                  >
                    <Box sx={{ height: 190, backgroundImage: `url("${makeArtDataUri(post)}")`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                    <CardContent sx={{ p: 2.2 }}>
                      <Chip
                        icon={<Icon sx={{ color: `${style.accent} !important` }} />}
                        label={post.category}
                        sx={{
                          mb: 1.2,
                          borderRadius: 999,
                          backgroundColor: style.tint,
                          color: surface.title,
                        }}
                      />
                      <Typography sx={{ mb: 1, fontSize: '1.1rem', lineHeight: 1.3, fontWeight: 900, color: surface.title }}>
                        {post.title}
                      </Typography>
                      <Typography sx={{ color: surface.body, lineHeight: 1.75, mb: 1.4 }}>
                        {post.summary}
                      </Typography>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontSize: '0.82rem', color: surface.muted }}>{post.readTime}</Typography>
                        <Button size="small" endIcon={<ArrowOutward />} sx={{ textTransform: 'none', fontWeight: 800 }}>
                          Open article
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>

            {filteredPosts.length === 0 && (
              <Card sx={{ borderRadius: 5, border: '1px solid', borderColor: surface.border, backgroundColor: surface.panel }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ fontWeight: 900, color: surface.title, mb: 0.8 }}>No articles found</Typography>
                  <Typography sx={{ color: surface.body, lineHeight: 1.75 }}>
                    Try a broader term such as carrier, authority, insurance, FMCSA, or fraud.
                  </Typography>
                </CardContent>
              </Card>
            )}

            <Card
              sx={{
                borderRadius: 6,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: surface.border,
                backgroundColor: isDark ? '#0f172a' : '#ffffff',
                backdropFilter: 'blur(14px)',
                boxShadow: surface.shadow,
              }}
            >
              <Box sx={{ height: { xs: 240, md: 320 }, backgroundImage: `url("${makeArtDataUri(activePost)}")`, backgroundSize: 'cover', backgroundPosition: 'center' }} />

              <Box sx={{ p: { xs: 3, md: 4 } }}>
                <Button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  startIcon={<ArrowBack />}
                  sx={{ mb: 1.5, px: 0, textTransform: 'none', fontWeight: 700 }}
                >
                  Back to top
                </Button>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1fr) 300px' },
                    gap: 4,
                    alignItems: 'start',
                  }}
                >
                  <Box sx={{ maxWidth: 820 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, color: surface.muted, flexWrap: 'wrap' }} useFlexGap>
                      <Typography component="a" href="/" sx={{ color: surface.muted, textDecoration: 'none', fontSize: '0.9rem' }}>
                        Home
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem' }}>/</Typography>
                      <Typography component="a" href="/blog" sx={{ color: surface.muted, textDecoration: 'none', fontSize: '0.9rem' }}>
                        Blog
                      </Typography>
                      <Typography sx={{ fontSize: '0.9rem' }}>/</Typography>
                      <Typography sx={{ fontSize: '0.9rem', color: surface.body }}>{activePost.title}</Typography>
                    </Stack>

                    <Box
                      sx={{
                        mb: 3,
                        pb: 3,
                        borderBottom: '1px solid',
                        borderColor: surface.border,
                      }}
                    >
                      <Chip
                        label={activePost.category}
                        sx={{
                          mb: 1.4,
                          borderRadius: 999,
                          fontWeight: 900,
                          color: '#fff',
                          background: activeStyle.gradient,
                        }}
                      />
                      <Typography
                        component="h2"
                        sx={{
                          fontSize: { xs: '2.1rem', md: '3.6rem' },
                          lineHeight: 1.02,
                          fontWeight: 900,
                          letterSpacing: '-0.06em',
                          color: surface.title,
                          fontFamily: '"Sora", sans-serif',
                          mb: 1.2,
                        }}
                      >
                        {activePost.title}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.4, color: surface.muted }}>
                        <Typography sx={{ fontSize: '0.95rem' }}>By {AUTHOR_NAME}</Typography>
                        <Typography sx={{ fontSize: '0.95rem' }}>•</Typography>
                        <Typography sx={{ fontSize: '0.95rem' }}>{activePost.published}</Typography>
                        <Typography sx={{ fontSize: '0.95rem' }}>•</Typography>
                        <Typography sx={{ fontSize: '0.95rem' }}>{activePost.readTime}</Typography>
                      </Stack>
                      <Typography sx={{ maxWidth: 740, color: surface.body, lineHeight: 1.95, fontSize: { xs: '1rem', md: '1.08rem' } }}>
                        {activePost.excerpt}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1.25,
                        p: 2,
                        mb: 3,
                        borderRadius: 3,
                        backgroundColor: isDark ? alpha('#2563eb', 0.12) : '#eff6ff',
                        border: '1px solid',
                        borderColor: isDark ? alpha('#60a5fa', 0.22) : '#bfdbfe',
                      }}
                    >
                      <CheckCircleOutline sx={{ mt: '2px', color: '#2563eb' }} />
                      <Box>
                        <Typography sx={{ fontWeight: 800, color: surface.title, mb: 0.5 }}>Quick Tip</Typography>
                        <Typography
                          sx={{ color: surface.body, lineHeight: 1.8 }}
                        >
                          You can instantly look up any carrier by DOT or MC number using the CarrierCheck search tool before making a booking decision.
                        </Typography>
                      </Box>
                    </Box>

                    <Card sx={{ mb: 3, borderRadius: 4, border: '1px solid', borderColor: surface.border, backgroundColor: surface.soft }}>
                      <CardContent sx={{ p: 2.2 }}>
                        <Typography sx={{ mb: 1, fontWeight: 900, color: surface.title }}>What you will learn</Typography>
                        <Stack spacing={0.9}>
                          {activePost.learn.map((item) => (
                            <Typography key={item} sx={{ color: surface.body, lineHeight: 1.75 }}>
                              {item}
                            </Typography>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>

                    {activePost.steps && (
                      <Box sx={{ mb: 3 }}>
                        <Typography sx={{ mb: 1.3, fontSize: '1.15rem', fontWeight: 900, color: surface.title }}>Recommended sequence</Typography>
                        <Stack spacing={1.1}>
                          {activePost.steps.map((step, index) => (
                            <Box
                              key={step}
                              sx={{
                                display: 'flex',
                                gap: 1.4,
                                p: 1.8,
                                borderRadius: 3.5,
                                backgroundColor: surface.soft,
                                border: '1px solid',
                                borderColor: surface.border,
                              }}
                            >
                              <Box
                                sx={{
                                  minWidth: 34,
                                  height: 34,
                                  borderRadius: '50%',
                                  display: 'grid',
                                  placeItems: 'center',
                                  color: '#fff',
                                  fontWeight: 900,
                                  background: activeStyle.gradient,
                                }}
                              >
                                {index + 1}
                              </Box>
                              <Typography sx={{ color: surface.body, lineHeight: 1.8 }}>{step}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    <Box sx={{ maxWidth: 860 }}>
                      <Stack spacing={2.5}>
                        {articleSections.map((section) => (
                          <Box
                            key={section.heading}
                            id={section.slug}
                            sx={{
                              p: { xs: 2.3, md: 2.8 },
                              borderRadius: 4,
                              backgroundColor: surface.soft,
                              border: '1px solid',
                              borderColor: surface.border,
                            }}
                          >
                            <Typography sx={{ mb: 1.25, fontSize: { xs: '1.1rem', md: '1.28rem' }, fontWeight: 900, color: surface.title }}>
                              {section.heading}
                            </Typography>
                            <Stack spacing={1.2}>
                              {section.body.map((paragraph) => (
                                <Typography key={paragraph} sx={{ color: surface.body, lineHeight: 1.95 }}>
                                  {paragraph}
                                </Typography>
                              ))}
                            </Stack>
                            {section.checklist && (
                              <Stack spacing={1} sx={{ mt: 1.7 }}>
                                {section.checklist.map((item) => {
                                  const tone = getChecklistTone(item);
                                  return (
                                    <Box
                                      key={item}
                                      sx={{
                                        display: 'flex',
                                        gap: 1,
                                        p: 1.1,
                                        borderRadius: 2.5,
                                        backgroundColor: tone === 'success' ? alpha('#10b981', 0.09) : alpha('#f59e0b', 0.10),
                                      }}
                                    >
                                      {tone === 'success' ? (
                                        <CheckCircleOutline sx={{ color: '#10b981', fontSize: 20, mt: '2px' }} />
                                      ) : (
                                        <ReportProblemOutlined sx={{ color: '#f59e0b', fontSize: 20, mt: '2px' }} />
                                      )}
                                      <Typography sx={{ color: surface.body, lineHeight: 1.75 }}>{item}</Typography>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </Box>

                    {activePost.table && (
                      <TableContainer
                        sx={{
                          mt: 3,
                          borderRadius: 4,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: surface.border,
                          backgroundColor: surface.soft,
                        }}
                      >
                        <Table>
                          <TableHead sx={{ backgroundColor: isDark ? alpha('#ffffff', 0.05) : '#eef2ff' }}>
                            <TableRow>
                              {activePost.table.headers.map((header) => (
                                <TableCell key={header} sx={{ fontWeight: 900, color: surface.title }}>
                                  {header}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {activePost.table.rows.map((row) => (
                              <TableRow key={row.join('-')}>
                                {row.map((cell) => (
                                  <TableCell key={cell} sx={{ color: surface.body, lineHeight: 1.75, verticalAlign: 'top' }}>
                                    {cell}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}

                    <Box
                      sx={{
                        mt: 3,
                        p: { xs: 2.2, md: 2.6 },
                        borderRadius: 4,
                        borderLeft: '6px solid',
                        borderLeftColor: activeStyle.accent,
                        backgroundColor: isDark ? alpha('#ffffff', 0.03) : '#ffffff',
                        borderTop: '1px solid',
                        borderRight: '1px solid',
                        borderBottom: '1px solid',
                        borderColor: surface.border,
                      }}
                    >
                      <Typography sx={{ mb: 1, fontWeight: 900, color: surface.title }}>Key takeaways</Typography>
                      <Stack spacing={0.8}>
                        {activePost.takeaway.map((item) => (
                          <Typography key={item} sx={{ color: surface.body, lineHeight: 1.8 }}>
                            {item}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>

                    <Alert
                      severity={activePost.warningTitle === 'Warning' ? 'warning' : 'info'}
                      sx={{ mt: 3, borderRadius: 3, '& .MuiAlert-message': { width: '100%' } }}
                    >
                      <Typography sx={{ mb: 0.5, fontWeight: 900 }}>{activePost.warningTitle}</Typography>
                      <Typography sx={{ lineHeight: 1.8 }}>{activePost.warningText}</Typography>
                    </Alert>

                    <Box
                      sx={{
                        mt: 3,
                        p: { xs: 2.2, md: 2.6 },
                        borderRadius: 4,
                        backgroundColor: isDark ? '#111827' : '#f8fafc',
                        border: '1px solid',
                        borderColor: surface.border,
                      }}
                    >
                      <Typography sx={{ mb: 0.8, fontWeight: 900, color: surface.title }}>Look up any carrier by DOT or MC number</Typography>
                      <Typography sx={{ mb: 1.5, color: surface.body, lineHeight: 1.75 }}>
                        Free lookup with no sign up required. Check authority, insurance, and public FMCSA details in seconds.
                      </Typography>
                      <Button href="/" variant="contained" sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800, background: 'linear-gradient(135deg, #2563eb 0%, #0f766e 100%)' }}>
                        Search a Carrier
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        mt: 3,
                        p: { xs: 2.2, md: 2.6 },
                        borderRadius: 4,
                        backgroundColor: isDark ? '#0b1323' : '#ffffff',
                        border: '1px solid',
                        borderColor: surface.border,
                        display: 'flex',
                        gap: 1.4,
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          color: '#fff',
                          fontWeight: 900,
                          background: 'linear-gradient(135deg, #0f172a 0%, #2563eb 100%)',
                        }}
                      >
                        KD
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 900, color: surface.title }}>{AUTHOR_NAME}</Typography>
                        <Typography sx={{ color: surface.body, lineHeight: 1.7 }}>
                          A trucking dispatch company with firsthand experience in carrier vetting and freight operations.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Stack spacing={2} sx={{ position: isCompact ? 'static' : 'sticky', top: 108 }}>
                    <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: surface.border, backgroundColor: surface.soft }}>
                      <CardContent sx={{ p: 2 }}>
                        <Typography sx={{ mb: 1.2, fontWeight: 900, color: surface.title }}>In This Article</Typography>
                        <Stack spacing={0.8}>
                          {articleSections.map((section, index) => (
                            <Button
                              key={section.slug}
                              href={`#${section.slug}`}
                              sx={{
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                px: 0,
                                py: 0.2,
                                textTransform: 'none',
                                color: surface.body,
                              }}
                            >
                              {index + 1}. {section.heading}
                            </Button>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>

                    <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: isDark ? '#111827' : '#0f172a', backgroundColor: isDark ? '#111827' : '#0f172a' }}>
                      <CardContent sx={{ p: 2 }}>
                        <Typography sx={{ mb: 1.1, fontWeight: 900, color: 'rgba(255,255,255,0.82)' }}>Free Carrier Lookup</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.7, mb: 1.5 }}>
                          Instantly verify any US carrier by DOT or MC number.
                        </Typography>
                        <Button href="/" fullWidth variant="contained" sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800, backgroundColor: '#14b8a6' }}>
                          Search a Carrier
                        </Button>
                      </CardContent>
                    </Card>

                    <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: surface.border, backgroundColor: surface.soft }}>
                      <CardContent sx={{ p: 2 }}>
                        <Typography sx={{ mb: 1.1, fontWeight: 900, color: surface.title }}>Related Articles</Typography>
                        <Stack spacing={1.2}>
                          {relatedPosts.map((post) => (
                            <Button
                              key={post.id}
                              onClick={() => openPost(post.id)}
                              sx={{
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                p: 0,
                                textTransform: 'none',
                                color: surface.body,
                              }}
                            >
                              <Box>
                                <Typography sx={{ fontWeight: 800, lineHeight: 1.45 }}>{post.title}</Typography>
                                <Typography sx={{ fontSize: '0.82rem', color: surface.muted }}>{post.readTime}</Typography>
                              </Box>
                            </Button>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Stack>
                </Box>
              </Box>
            </Card>

            <Divider sx={{ borderColor: surface.border }} />

            <Box>
              <Typography sx={{ mb: 2, fontSize: '1.35rem', fontWeight: 900, color: surface.title, fontFamily: '"Sora", sans-serif' }}>
                More Articles
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2.2 }}>
                {morePosts.map((post) => {
                  const style = categoryStyles[post.category];
                  const Icon = style.Icon;
                  return (
                    <Card
                      key={post.id}
                      onClick={() => openPost(post.id)}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 5,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: surface.border,
                        backgroundColor: surface.panel,
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: surface.shadow,
                        },
                      }}
                    >
                      <Box sx={{ height: 170, backgroundImage: `url("${makeArtDataUri(post)}")`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                      <CardContent sx={{ p: 2.1 }}>
                        <Chip
                          icon={<Icon sx={{ color: `${style.accent} !important` }} />}
                          label={post.category}
                          sx={{ mb: 1.1, borderRadius: 999, backgroundColor: style.tint }}
                        />
                        <Typography sx={{ mb: 1, fontWeight: 900, lineHeight: 1.35, color: surface.title }}>
                          {post.title}
                        </Typography>
                        <Typography sx={{ color: surface.body, lineHeight: 1.78 }}>
                          {post.summary}
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Box>
          </Stack>
        </Box>

        <Box
          component="footer"
          sx={{
            mt: 5,
            pt: 3,
            pb: 6,
            borderTop: '1px solid',
            borderColor: surface.border,
            color: surface.muted,
          }}
        >
          <Typography sx={{ mb: 0.8, color: surface.body }}>
            {AUTHOR_NAME} · {SITE_NAME} is a free tool by {AUTHOR_NAME}
          </Typography>
          <Typography sx={{ color: surface.muted }}>
            Data sourced from FMCSA · Privacy Policy · About · Contact
          </Typography>
        </Box>
      </Container>

      <Box
        component="a"
        href="https://chat.whatsapp.com/ErUqKxOJ5NREw68gG5OBIk"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Join our WhatsApp group"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 58,
          height: 58,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: '#25D366',
          color: '#fff',
          textDecoration: 'none',
          boxShadow: '0 16px 36px rgba(37,211,102,0.35)',
          zIndex: 40,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px) scale(1.04)',
            boxShadow: '0 22px 42px rgba(37,211,102,0.44)',
          },
        }}
      >
        <Box sx={{ fontSize: '1.5rem', fontWeight: 900 }}>W</Box>
      </Box>
    </Box>
  );
};

export default BlogPage;
